<template>
    <div>
        <slot
            :paged-items="pagedItems"
            :total="total"
            :from="from"
            :to="to"
            :page="currentPage"
            :last-page="lastPage"
        />

        <div
            v-if="total > 0"
            class="mt-4 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between"
        >
            <p class="text-base text-gray-700">
                Showing {{ from }} to {{ to }} of {{ total }} results
            </p>

            <div class="flex items-center gap-3 self-start lg:self-auto">
                <select
                    v-if="showPageSize"
                    v-model.number="currentPageSize"
                    class="h-12 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-600"
                >
                    <option
                        v-for="size in pageSizeOptions"
                        :key="size"
                        :value="size"
                    >
                        {{ size }}
                    </option>
                </select>

                <div
                    class="inline-flex h-10 items-stretch overflow-hidden rounded-lg border border-gray-300 bg-white shadow-sm"
                >
                    <button
                        class="flex min-w-10 items-center justify-center border-r border-gray-300 px-3 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700 focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-300 disabled:cursor-not-allowed disabled:text-gray-300"
                        :disabled="currentPage <= 1"
                        @click="prevPage"
                    >
                        <SvgIcon icon="arrow-left" size="xs" weight="bold" />
                    </button>

                    <template v-for="(p, i) in pageNumbers" :key="i">
                        <span
                            v-if="p === '...'"
                            class="flex min-w-10 items-center justify-center border-r border-gray-300 px-3 text-lg text-gray-500"
                        >
                            ...
                        </span>
                        <button
                            v-else
                            :class="[
                                'flex min-w-10 items-center justify-center border-r border-gray-300 px-3 text-sm transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-300',
                                p === currentPage
                                    ? 'font-medium text-blue-600 underline decoration-1 underline-offset-2'
                                    : 'text-gray-600',
                            ]"
                            @click="goToPage(Number(p))"
                        >
                            {{ p }}
                        </button>
                    </template>

                    <button
                        class="flex min-w-10 items-center justify-center px-3 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700 focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-300 disabled:cursor-not-allowed disabled:text-gray-300"
                        :disabled="currentPage >= lastPage"
                        @click="nextPage"
                    >
                        <SvgIcon icon="arrow-right" size="xs" weight="bold" />
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
export default {
    props: {
        items: {
            type: Array as () => any[],
            default: () => [],
        },
        initialPage: {
            type: Number,
            default: 1,
        },
        initialPageSize: {
            type: Number,
            default: 10,
        },
        pageSizeOptions: {
            type: Array as () => number[],
            default: () => [10, 25, 50],
        },
        showPageSize: {
            type: Boolean,
            default: true,
        },
    },
    emits: ["page-change", "page-size-change"],
    data(): any {
        return {
            currentPage: this.initialPage,
            currentPageSize: this.initialPageSize,
        };
    },
    computed: {
        total(): number {
            return this.items.length;
        },
        lastPage(): number {
            return Math.max(1, Math.ceil(this.total / this.currentPageSize));
        },
        from(): number {
            if (this.total === 0) {
                return 0;
            }

            return (this.currentPage - 1) * this.currentPageSize + 1;
        },
        to(): number {
            return Math.min(
                this.currentPage * this.currentPageSize,
                this.total,
            );
        },
        pagedItems(): any[] {
            const start = (this.currentPage - 1) * this.currentPageSize;
            return this.items.slice(start, start + this.currentPageSize);
        },
        pageNumbers(): (number | string)[] {
            const total = this.lastPage;
            const current = this.currentPage;

            if (total <= 10) {
                return Array.from({ length: total }, (_, i) => i + 1);
            }

            const delta = 2;
            const result: (number | string)[] = [1];
            const left = Math.max(2, current - delta);
            const right = Math.min(total - 1, current + delta);

            if (left > 2) {
                result.push("...");
            }

            for (let i = left; i <= right; i++) {
                result.push(i);
            }

            if (right < total - 1) {
                result.push("...");
            }

            result.push(total);

            return result;
        },
    },
    watch: {
        items() {
            if (this.currentPage > this.lastPage) {
                this.currentPage = this.lastPage;
            }

            if (this.currentPage < 1) {
                this.currentPage = 1;
            }
        },
        currentPage(newPage: number) {
            this.$emit("page-change", newPage);
        },
        currentPageSize(newSize: number) {
            this.currentPage = 1;
            this.$emit("page-size-change", newSize);
        },
    },
    methods: {
        goToPage(page: number): void {
            if (page < 1 || page > this.lastPage) {
                return;
            }

            this.currentPage = page;
        },
        prevPage(): void {
            this.goToPage(this.currentPage - 1);
        },
        nextPage(): void {
            this.goToPage(this.currentPage + 1);
        },
    },
};
</script>
